import type { GhLoggedCookie } from "@/types/gh-component"

/**
 * Cookie names written by the G-H-C web component.
 *
 * The component sets these with `path` and `secure` only — it never passes a
 * `domain` — so they land as first-party cookies on whatever host serves the
 * page. That is what makes them readable from the Next.js server.
 *
 * The prefix is environment-specific (`dt_pre` on preproduction). It is a
 * `NEXT_PUBLIC_` var so the client bundle and the server resolve it from the
 * same source.
 */
export const GH_COOKIE_PREFIX =
  process.env.NEXT_PUBLIC_GHC_COOKIE_PREFIX || "dt_pre"

export const GH_TOKEN_COOKIE = `${GH_COOKIE_PREFIX}token`
export const GH_ANONYMOUS_TOKEN_COOKIE = `${GH_COOKIE_PREFIX}token_anonymous`
export const GH_REFRESH_TOKEN_COOKIE = `${GH_COOKIE_PREFIX}refresh_token`
export const GH_WAS_LOGGED_COOKIE = `${GH_COOKIE_PREFIX}was_logged`

/** Every cookie the component owns, for teardown on sign-out. */
export const GH_COOKIES = [
  GH_TOKEN_COOKIE,
  GH_ANONYMOUS_TOKEN_COOKIE,
  GH_REFRESH_TOKEN_COOKIE,
  GH_WAS_LOGGED_COOKIE,
]

export function parseGhTokenCookie(
  rawValue: string | undefined | null
): GhLoggedCookie | null {
  if (!rawValue) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(rawValue)
  } catch {
    return null
  }

  if (!parsed || typeof parsed !== "object") return null

  const candidate = parsed as Partial<GhLoggedCookie>
  const userId = candidate.user_id

  if (typeof userId !== "string" && typeof userId !== "number") return null
  if (String(userId).length === 0) return null
  if (typeof candidate.access_token !== "string" || !candidate.access_token) {
    return null
  }

  return {
    user_id: userId,
    favourites: candidate.favourites ?? null,
    token_type: candidate.token_type || "Bearer",
    access_token: candidate.access_token,
    cookiePrefix: candidate.cookiePrefix,
  }
}

export function parseCookieHeader(
  cookieHeader?: string | null
): Map<string, string> {
  const cookieMap = new Map<string, string>()
  if (!cookieHeader) return cookieMap

  for (const cookie of cookieHeader.split(";")) {
    const separatorIndex = cookie.indexOf("=")
    if (separatorIndex === -1) continue

    const name = cookie.slice(0, separatorIndex).trim()
    const value = cookie.slice(separatorIndex + 1).trim()
    if (!name) continue

    try {
      cookieMap.set(name, decodeURIComponent(value))
    } catch {
      cookieMap.set(name, value)
    }
  }

  return cookieMap
}
